import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Field, SectionTitle, Pill, Thumb, Cover } from "@/components/Bits";
import NotesPanel from "@/components/NotesPanel";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";
const blank = { name: "", company: "", email: "", phone: "", billing_address: "", project_address: "", notes: "", portal_enabled: false };

export function Clients() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setForm(blank);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="clients-page">
      <PageHeader
        overline="Studio"
        title="Clients"
        subtitle="Client records, contacts and portal access."
        actions={
          <button data-testid="add-client-btn" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Client
          </button>
        }
      />

      {open && (
        <form onSubmit={submit} className="card-surface grid gap-5 p-7 sm:grid-cols-2" data-testid="client-form">
          <Field label="Client Name"><input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} /></Field>
          <Field label="Company"><input data-testid="client-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={input} /></Field>
          <Field label="Email"><input data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} /></Field>
          <Field label="Phone"><input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} /></Field>
          <Field label="Billing Address"><input data-testid="client-billing-input" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className={input} /></Field>
          <Field label="Project Address"><input data-testid="client-project-address-input" value={form.project_address} onChange={(e) => setForm({ ...form, project_address: e.target.value })} className={input} /></Field>
          <Field label="Notes" className="sm:col-span-2"><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={input} /></Field>
          <label className="flex items-center gap-3 text-sm text-ink sm:col-span-2">
            <input data-testid="client-portal-toggle" type="checkbox" checked={form.portal_enabled} onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })} className="h-4 w-4 accent-[#BEC221]" />
            Client portal enabled
          </label>
          <div className="sm:col-span-2">
            <button data-testid="client-submit-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Client</button>
          </div>
        </form>
      )}

      {clients.length === 0 ? (
        <EmptyState testId="clients-empty" title="No clients yet" body="Add a client to start a project." />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="clients-grid">
          {clients.map((c, i) => (
            <Link key={c.id} to={`/clients/${c.id}`} data-testid={`client-card-${c.id}`} className="card-surface card-hover animate-rise p-6" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-serif text-xl text-ink">{c.name}</h3>
                {c.portal_enabled && <Pill tone="bg-lime text-ink">Portal</Pill>}
              </div>
              {c.company && <div className="mt-1 overline">{c.company}</div>}
              <div className="mt-5 space-y-1 text-sm text-stone">
                {c.email && <div className="truncate">{c.email}</div>}
                {c.phone && <div className="num">{c.phone}</div>}
              </div>
              <div className="num mt-5 border-t border-line pt-4 text-xs text-fog">{c.project_count ?? 0} projects</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [client, setClient] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => { setClient(r.data); setForm(r.data); }).catch((e) => toast.error(apiError(e)));
  }, [id]);

  if (!client) return <div className="py-24 text-center text-sm text-fog">Loading client…</div>;

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/clients/${id}`, form);
      setClient((c) => ({ ...c, ...data }));
      toast.success("Client updated");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Clients
      </button>
      <div className="border-b border-line pb-8">
        {client.company && <div className="overline mb-3">{client.company}</div>}
        <h1 data-testid="client-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{client.name}</h1>
        <div className="mt-4 flex flex-wrap gap-6 text-sm text-stone">
          {client.email && <span>{client.email}</span>}
          {client.phone && <span className="num">{client.phone}</span>}
          <Pill tone={client.portal_enabled ? "bg-lime text-ink" : "bg-line text-stone"}>
            Portal {client.portal_enabled ? "enabled" : "disabled"}
          </Pill>
        </div>
      </div>

      {isStaff && (
        <form onSubmit={save} className="card-surface grid gap-5 p-7 sm:grid-cols-2" data-testid="client-edit-form">
          {[["name", "Client Name"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["billing_address", "Billing Address"], ["project_address", "Project Address"]].map(([k, l]) => (
            <Field key={k} label={l}>
              <input data-testid={`client-edit-${k.replace(/_/g, "-")}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} />
            </Field>
          ))}
          <Field label="Notes" className="sm:col-span-2">
            <textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={input} />
          </Field>
          <label className="flex items-center gap-3 text-sm text-ink sm:col-span-2">
            <input data-testid="client-edit-portal" type="checkbox" checked={!!form.portal_enabled} onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })} className="h-4 w-4 accent-[#BEC221]" />
            Client portal enabled
          </label>
          <div className="sm:col-span-2">
            <button data-testid="client-save-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Client</button>
          </div>
        </form>
      )}

      <div>
        <SectionTitle>Projects</SectionTitle>
        {(client.projects || []).length === 0 ? (
          <EmptyState testId="client-projects-empty" title="No projects" body="Create a project for this client." />
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="client-projects">
            {client.projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`client-project-${p.id}`} className="card-surface card-hover overflow-hidden">
                <div className="aspect-[16/10] bg-line">
                  {p.cover_file_id || p.cover_image ? <Cover project={p} className="h-full w-full" /> : <Thumb className="h-full w-full" rounded="rounded-none" />}
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-lg text-ink">{p.name}</h3>
                  <div className="mt-1 text-sm text-stone">{p.project_type} · {p.status}</div>
                  <div className="num mt-4 text-sm text-ink">{money(p.budget_total)}</div>
                  <div className="mt-1 text-xs text-fog">Target {dateShort(p.target_completion_date)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {isStaff && (
        <>
          <div>
            <SectionTitle>Client Logins</SectionTitle>
            <div className="card-surface divide-y divide-line">
              {(client.users || []).map((u) => (
                <div key={u.id} className="flex items-center justify-between px-6 py-4 text-sm">
                  <span className="text-ink">{u.name} — {u.email}</span>
                  <Pill tone="bg-[#EDEFC4] text-ink">{u.role}</Pill>
                </div>
              ))}
              {!(client.users || []).length && <div className="px-6 py-8 text-center text-sm text-fog">No client logins linked yet.</div>}
            </div>
          </div>
          <div className="card-surface p-7">
            <NotesPanel entityType="client" entityId={id} title="Client Internal Notes" />
          </div>
        </>
      )}
    </div>
  );
}
